/**
 * B632 (e) — the guest's CHANGE sheet (650:165): the pure half. What moves
 * (every line on the order's first night), the schedule's showtimes by the
 * slot's own local day off `slot.fit`, and the summary. `order.move` is the
 * one write (the buyer's own order, recorded default (i)); same price only,
 * so the difference is always nothing.
 */
import { formatMoney } from "./money.js";
import { showtimeWords, type MeOrderDetail } from "./me.js";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** B632 (e) — what a move carries: every line on the order's first night (the op moves a line; the party moves together). */
export interface MeMoveTarget {
  readonly fromSlotId: string;
  readonly scheduleId: string;
  readonly productId: string;
  readonly lineIds: readonly string[];
  /** B838 — lineId → its tickets, so a party split by a refusal mid-move can be said in tickets. */
  readonly lineCounts: Readonly<Record<string, number>>;
  /** variantId → count — the party `slot.fit` judges each showtime for. */
  readonly party: Readonly<Record<string, number>>;
  readonly count: number;
  /** "2 × General Admission". */
  readonly what: string;
}

/** A slot id's own local occurrence (`<scheduleId>#YYYY-MM-DDTHH:MM`) — no zone arithmetic. */
function occurrenceOf(slotId: string): { readonly scheduleId: string; readonly date: string; readonly time: string } | undefined {
  const m = /^(.+)#(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/.exec(slotId);
  return m === null ? undefined : { scheduleId: m[1]!, date: m[2]!, time: m[3]! };
}

export function meMoveTarget(order: Pick<MeOrderDetail, "lines">): MeMoveTarget | undefined {
  const slotted = order.lines.filter((l) => l.slotId !== undefined && occurrenceOf(l.slotId) !== undefined);
  const first = [...slotted].sort((a, b) => a.slotId!.slice(a.slotId!.indexOf("#")).localeCompare(b.slotId!.slice(b.slotId!.indexOf("#"))))[0];
  if (first === undefined) return undefined;
  const lines = slotted.filter((l) => l.slotId === first.slotId);
  const party: Record<string, number> = {};
  const counts = new Map<string, number>();
  for (const l of lines) {
    if (l.variantId !== undefined && l.productId === first.productId) party[l.variantId] = (party[l.variantId] ?? 0) + l.quantity;
    const name = l.variantName ?? "Ticket";
    counts.set(name, (counts.get(name) ?? 0) + l.quantity);
  }
  return {
    fromSlotId: first.slotId!,
    scheduleId: occurrenceOf(first.slotId!)!.scheduleId,
    productId: first.productId,
    lineIds: lines.map((l) => l.lineId),
    lineCounts: Object.fromEntries(lines.map((l) => [l.lineId, l.quantity])),
    party,
    count: lines.reduce((n, l) => n + l.quantity, 0),
    what: [...counts].map(([name, n]) => `${String(n)} × ${name}`).join(" · "),
  };
}

/** One `slot.fit` row, as the sheet reads it. */
export interface MeFitSlot {
  readonly slotId: string;
  readonly start: number;
  readonly status: string;
  readonly fits?: boolean;
  readonly pools: readonly { readonly poolId: string; readonly available: number }[];
}

export interface MeMoveRow {
  readonly slotId: string;
  /** "7:00 PM". */
  readonly time: string;
  /** "your current showtime" · "14 left" · "sold out". */
  readonly detail: string;
  readonly open: boolean;
}

export interface MeMoveDay {
  /** "Fri 9". */
  readonly day: string;
  readonly rows: readonly MeMoveRow[];
}

const clockOf = (time: string): string => showtimeWords("2026-01-01", time).split(" · ")[1]!;
const shortDay = (date: string): string => {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return `${DAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]!} ${String(d)}`;
};

/**
 * 650:165 — the schedule's showtimes by the slot's own local day, soonest
 * first. The night being left keeps its place and cannot be picked; one the
 * party does not fit says sold out; a started, closed or drafted showtime is
 * not offered at all.
 */
export function meMoveChoices(slots: readonly MeFitSlot[], fromSlotId: string, nowMs: number, unit: string): readonly MeMoveDay[] {
  const rows = slots
    .filter((s) => s.slotId === fromSlotId || (s.status === "ON_SALE" && s.start > nowMs))
    .map((s) => ({ s, at: occurrenceOf(s.slotId) }))
    .filter((r): r is { s: MeFitSlot; at: NonNullable<ReturnType<typeof occurrenceOf>> } => r.at !== undefined)
    .sort((a, b) => `${a.at.date}${a.at.time}`.localeCompare(`${b.at.date}${b.at.time}`));
  const days: { day: string; date: string; rows: MeMoveRow[] }[] = [];
  for (const { s, at } of rows) {
    const left = s.pools.reduce((n, p) => n + p.available, 0);
    const current = s.slotId === fromSlotId;
    const full = s.fits === false || left === 0;
    const row: MeMoveRow = {
      slotId: s.slotId,
      time: clockOf(at.time),
      detail: current ? `your current ${unit}` : full ? "sold out" : `${String(left)} left`,
      open: !current && !full,
    };
    const last = days[days.length - 1];
    if (last?.date === at.date) last.rows.push(row);
    else days.push({ day: shortDay(at.date), date: at.date, rows: [row] });
  }
  return days.map(({ day, rows: r }) => ({ day, rows: r }));
}

/** "Sat 10 · 7:00 PM" — the door's words for where the party is going. */
export function meMoveWhere(slotId: string): string {
  const at = occurrenceOf(slotId);
  return at === undefined ? "" : `${shortDay(at.date)} · ${clockOf(at.time)}`;
}

/** 650:165's summary: what moves, from → to, the price difference (none — same price only, the op's v1), where the new tickets go. */
export function meMoveSummary(target: MeMoveTarget, toSlotId: string, currency: string): readonly { readonly label: string; readonly value: string }[] {
  const short = (slotId: string): string => {
    const at = occurrenceOf(slotId);
    return at === undefined ? "" : `${DAYS[new Date(`${at.date}T00:00:00Z`).getUTCDay()]!} ${clockOf(at.time)}`;
  };
  return [
    { label: "Moving", value: target.what },
    { label: "From → to", value: `${short(target.fromSlotId)} → ${short(toSlotId)}` },
    { label: "Price difference", value: formatMoney({ amount: 0, currency }) },
    { label: "New tickets", value: "emailed to you" },
  ];
}

/**
 * B838 — the move is one `order.move` per line, so a refusal can land after
 * part of the party moved. `at` is where each line the sheet moved now sits
 * (absent = still on the night being left). One night for every line = no
 * split, nothing to say; otherwise how many moved, where, and where the rest
 * still are — so the buyer never reads "nothing changed" over a split party.
 */
export function meMoveSplitWords(target: MeMoveTarget, at: Readonly<Record<string, string>>): string | undefined {
  const where = (lineId: string): string => at[lineId] ?? target.fromSlotId;
  const nights = new Set(target.lineIds.map(where));
  if (nights.size < 2) return undefined;
  /* a second pick after a split can leave three nights: every one the party went to is named */
  const movedTo = [...nights].filter((s) => s !== target.fromSlotId);
  const tickets = (pred: (lineId: string) => boolean): number =>
    target.lineIds.filter(pred).reduce((n, l) => n + (target.lineCounts[l] ?? 0), 0);
  const moved = tickets((l) => where(l) !== target.fromSlotId);
  const left = tickets((l) => where(l) === target.fromSlotId);
  const words = `${String(moved)} of your ${String(target.count)} ${target.count === 1 ? "ticket" : "tickets"} moved to ${movedTo.map(meMoveWhere).join(" and ")}`;
  return left === 0 ? `${words}.` : `${words}; ${String(left)} ${left === 1 ? "is" : "are"} still on ${meMoveWhere(target.fromSlotId)}.`;
}

/** "Sat, Oct 10 · 7:00 PM" — the moved-to night, in the order page's card words. */
export function meMovedWords(slotId: string): string {
  const at = occurrenceOf(slotId);
  return at === undefined ? "" : showtimeWords(at.date, at.time);
}

/**
 * One day's rows as radio options. A row that cannot be picked (the night
 * being left, a sold-out one) says why on its own face — the hint IS the
 * reason ("your current showtime", "sold out"), so the shut option is never
 * silent (B296).
 */
export function meMoveOptions(day: MeMoveDay): readonly { readonly value: string; readonly label: string; readonly hint: string; readonly disabled?: boolean }[] {
  return day.rows.map((r) => ({ value: r.slotId, label: r.time, hint: r.detail, ...(r.open ? {} : { disabled: true }) }));
}
